import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, LinkedinIcon, TwitterIcon, MailIcon, AwardIcon, BookOpenIcon, BriefcaseIcon, UserIcon, CheckCircleIcon, UsersIcon, HeartIcon, TrendingUpIcon, GraduationCapIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
// Sample data for connections
const connectionsData = [{
  id: 1,
  name: 'Marco Rossi',
  role: 'Data Scientist',
  company: 'Intel',
  location: 'Milano, Italia',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  tags: ['AI', 'Machine Learning', 'ESG'],
  bio: "Esperto di intelligenza artificiale con focus su soluzioni sostenibili. Sviluppo algoritmi di machine learning per ottimizzare l'efficienza energetica e ridurre l'impatto ambientale.",
  experience: [{
    title: 'Data Scientist',
    company: 'Intel',
    period: '2019 - Presente',
    description: 'Sviluppo di algoritmi di machine learning per progetti di sostenibilità e ottimizzazione energetica.'
  }],
  groups: ['AI for Sustainability', 'Green Tech Network'],
  courses: [{
    name: 'Climate Change 101: cosa dice la scienza',
    completed: true,
    date: 'Maggio 2023'
  }],
  certifications: [{
    name: 'ESG Data Analysis Expert',
    issuer: 'Greenback',
    date: 'Maggio 2023'
  }],
  stats: {
    connections: '89',
    impact: '85%',
    efficacy: '82%'
  }
}];
// Default profile data (Alex Rossi)
const defaultProfile = {
  id: 0,
  name: 'Alex Rossi',
  role: 'Ricercatore PhD',
  company: 'Università di Milano',
  location: 'Milano, Italia',
  avatar: 'https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg',
  tags: ['Climate Science', 'Energia Rinnovabile', 'Ricerca Ambientale', 'Divulgazione'],
  bio: 'Ricercatore in scienze ambientali con focus su cambiamento climatico e transizione energetica. Mi dedico alla ricerca scientifica e alla divulgazione di soluzioni pratiche per la sostenibilità. Il mio obiettivo è rendere la scienza del clima accessibile e trasformarla in azioni concrete per un futuro più sostenibile.',
  experience: [{
    title: 'Ricercatore PhD',
    company: 'Università di Milano - Dipartimento di Scienze Ambientali',
    period: '2021 - Presente',
    description: 'Ricerca su modelli climatici e strategie di mitigazione. Focus su transizione energetica e valutazione di impatto ambientale di tecnologie rinnovabili.'
  }, {
    title: 'Research Assistant',
    company: 'Politecnico di Milano',
    period: '2019 - 2021',
    description: 'Supporto alla ricerca su efficienza energetica e sistemi di energia rinnovabile. Analisi dati e modellazione di scenari di decarbonizzazione.'
  }, {
    title: 'Divulgatore Scientifico',
    company: 'Freelance',
    period: '2020 - Presente',
    description: 'Comunicazione scientifica su temi ambientali e climatici attraverso articoli, workshop e conferenze pubbliche.'
  }],
  groups: ['Climate Research Network', 'Ricercatori per il Clima', 'Divulgazione Scientifica Italia'],
  courses: [{
    name: 'Climate Change 101: cosa dice la scienza',
    completed: true,
    date: 'Dicembre 2023'
  }, {
    name: 'Casa efficiente: energia, bollette e comfort',
    completed: true,
    date: 'Novembre 2023'
  }, {
    name: 'Mobilità sostenibile: muoversi meglio, spendere meno',
    completed: false,
    progress: 45
  }],
  certifications: [{
    name: 'Climate Science Communicator',
    issuer: 'Greenback Academy',
    date: 'Dicembre 2023'
  }, {
    name: 'Renewable Energy Systems Specialist',
    issuer: 'Greenback Academy',
    date: 'Novembre 2023'
  }],
  stats: {
    connections: '156',
    impact: '94%',
    efficacy: '91%'
  }
};
const PublicProfile = () => {
  const {
    userId
  } = useParams();
  const [profileData, setProfileData] = useState(defaultProfile);
  useEffect(() => {
    // If userId is provided, find the matching connection
    if (userId) {
      const connection = connectionsData.find(c => c.id === parseInt(userId));
      if (connection) {
        setProfileData({
          ...connection,
          stats: connection.stats || {
            connections: '0',
            impact: '0%',
            efficacy: '0%'
          }
        });
      }
    }
  }, [userId]);
  return <div className="min-h-screen bg-gray-50" data-id="element-2826">
      {/* Hero section with gradient background */}
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white" data-id="element-2827">
        {/* Topbar */}
        <div className="container mx-auto px-4 py-4 flex justify-between items-center" data-id="element-2828">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className="h-8 object-contain" data-id="element-2829" />
          <Link to="/" className="py-2 px-4 bg-white text-green-600 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm" data-id="element-2830">
            Torna alla Dashboard
          </Link>
        </div>

        {/* Profile Header */}
        <div className="container mx-auto px-4 pb-16 pt-8" data-id="element-2831">
          <div className="flex flex-col items-center" data-id="element-2832">
            {/* Profile image */}
            <div className="w-32 h-32 rounded-full border-4 border-white overflow-hidden mb-6 shadow-xl" data-id="element-2833">
              <img src={profileData.avatar} alt={profileData.name} className="w-full h-full object-cover" data-id="element-2834" />
            </div>

            {/* Profile info */}
            <h1 className="text-3xl font-semibold mb-2 text-center text-white" data-id="element-2835">
              {profileData.name}
            </h1>
            <p className="text-xl text-white/90 mb-2 text-center font-normal" data-id="element-2836">
              {profileData.role}
            </p>
            <p className="text-lg text-white/80 mb-4 text-center font-normal" data-id="element-2837">
              {profileData.company && `${profileData.company} • `}
              {profileData.location.split(',')[0]}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 justify-center mb-6 max-w-2xl" data-id="element-2838">
              {profileData.tags.map((tag, index) => <span key={index} className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-normal" data-id="element-2839">
                  {tag}
                </span>)}
            </div>

            {/* Social and Actions */}
            <div className="flex flex-col md:flex-row items-center gap-6 mt-4" data-id="element-2840">
              {/* Social links */}
              <div className="flex gap-3" data-id="element-2841">
                <button className="p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors" data-id="element-2842">
                  <LinkedinIcon size={20} strokeWidth={1.5} data-id="element-2843" />
                </button>
                <button className="p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors" data-id="element-2844">
                  <TwitterIcon size={20} strokeWidth={1.5} data-id="element-2845" />
                </button>
                <button className="p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors" data-id="element-2846">
                  <MailIcon size={20} strokeWidth={1.5} data-id="element-2847" />
                </button>
              </div>

              {/* Connect button */}
              <button className="py-2 px-6 bg-white text-green-600 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-lg" data-id="element-2848">
                Aggiungi alle Connessioni
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Stats Section */}
      <div className="bg-white border-b border-gray-200 shadow-sm" data-id="element-2849">
        <div className="container mx-auto px-4 py-6" data-id="element-2850">
          <div className="max-w-6xl mx-auto grid grid-cols-3 divide-x divide-gray-200" data-id="element-2851">
            <KpiCard icon={<UsersIcon size={18} strokeWidth={1.5} data-id="element-2853" />} value={profileData.stats.connections} label="Connessioni" trend="+12 questo mese" trendUp={true} data-id="element-2852" />
            <KpiCard icon={<HeartIcon size={18} strokeWidth={1.5} data-id="element-2855" />} value={profileData.stats.impact} label="Indice di Impatto" trend="Top 5% nella community" trendUp={true} data-id="element-2854" />
            <KpiCard icon={<TrendingUpIcon size={18} strokeWidth={1.5} data-id="element-2857" />} value={profileData.stats.efficacy} label="Efficacia Profilo" trend="+15% rispetto al benchmark" trendUp={true} data-id="element-2856" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8" data-id="element-2858">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2859">
          {/* Left Column - About & Experience */}
          <div className="md:col-span-2 space-y-6" data-id="element-2860">
            {/* About Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-2861">
              <div className="flex items-center mb-4" data-id="element-2862">
                <UserIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-2863" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-2864">
                  Chi sono
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed font-normal" data-id="element-2865">
                {profileData.bio}
              </p>
            </div>

            {/* Experience Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-2866">
              <div className="flex items-center mb-4" data-id="element-2867">
                <BriefcaseIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-2868" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-2869">
                  Esperienza professionale
                </h2>
              </div>
              <div className="space-y-4" data-id="element-2870">
                {profileData.experience.map((exp, index) => <div key={index} className={`border-l-2 ${index === 0 ? 'border-green-600' : 'border-gray-200'} pl-4 ${index < profileData.experience.length - 1 ? 'pb-4' : ''}`} data-id="element-2871">
                    <h3 className="font-medium text-gray-800" data-id="element-2872">{exp.title}</h3>
                    <p className="text-gray-600 text-sm font-normal" data-id="element-2873">
                      {exp.company} • {exp.period}
                    </p>
                    <p className="text-gray-700 mt-2 text-sm font-normal leading-relaxed" data-id="element-2874">
                      {exp.description}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Active Groups */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-2875">
              <div className="flex items-center mb-4" data-id="element-2876">
                <UsersIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-2877" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-2878">
                  Gruppi attivi
                </h2>
              </div>
              <div className="space-y-3" data-id="element-2879">
                {profileData.groups.map((group, index) => <GroupItem key={index} name={group} members={Math.floor(Math.random() * 30 + 20)} data-id="element-2880" />)}
              </div>
            </div>
          </div>

          {/* Right Column - Courses & Certifications */}
          <div className="space-y-6" data-id="element-2881">
            {/* Greenback Path */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-2882">
              <div className="flex items-center mb-4" data-id="element-2883">
                <BookOpenIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-2884" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-2885">
                  Percorso Formativo
                </h2>
              </div>
              <div className="space-y-4" data-id="element-2886">
                {profileData.courses.map((course, index) => <div key={index} className="flex items-start" data-id="element-2887">
                    <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-gray-100" data-id="element-2888">
                      <img src={course.name.includes('Climate') ? 'https://www.innovationnewsnetwork.com/wp-content/uploads/2020/03/image-1.jpg' : course.name.includes('Casa') ? 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80' : 'https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=800&q=80'} alt={course.name} className="w-full h-full object-cover" data-id="element-2889" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-2890">
                      <div className="flex items-center" data-id="element-2891">
                        {course.completed ? <CheckCircleIcon size={16} className="text-green-600 mr-1" strokeWidth={2} data-id="element-2892" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300 mr-1" data-id="element-2893"></div>}
                        <h3 className="font-medium text-gray-800 text-sm" data-id="element-2894">
                          {course.name}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 font-normal" data-id="element-2895">
                        {course.completed ? `Completato • ${course.date}` : `In corso • ${course.progress}% completato`}
                      </p>
                      {!course.completed && <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2" data-id="element-2896">
                          <div className="bg-green-600 h-1.5 rounded-full transition-all duration-500" style={{
                      width: `${course.progress}%`
                    }} data-id="element-2897"></div>
                        </div>}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Certifications */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-2898">
              <div className="flex items-center mb-4" data-id="element-2899">
                <AwardIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-2900" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-2901">
                  Certificazioni
                </h2>
              </div>
              <div className="space-y-4" data-id="element-2902">
                {profileData.certifications.map((cert, index) => <div key={index} className="border border-gray-200 rounded-lg p-3 hover:border-green-600 transition-colors" data-id="element-2903">
                    <div className="flex items-start" data-id="element-2904">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-2905">
                        <GraduationCapIcon size={18} className="text-green-600" strokeWidth={1.5} data-id="element-2906" />
                      </div>
                      <div className="flex-1" data-id="element-2907">
                        <h3 className="font-medium text-gray-800 text-sm" data-id="element-2908">
                          {cert.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 font-normal" data-id="element-2909">
                          {cert.issuer} • Rilasciato {cert.date}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Connect with Greenback */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl shadow-sm p-6 text-white" data-id="element-2910">
              <h2 className="text-xl font-semibold mb-2" data-id="element-2911">
                Vuoi crescere come {profileData.name.split(' ')[0]}?
              </h2>
              <p className="text-sm mb-4 text-white/90 font-normal leading-relaxed" data-id="element-2912">
                Scopri i percorsi formativi Greenback e unisciti alla community
                di professionisti della sostenibilità.
              </p>
              <button className="w-full py-2 bg-white text-green-600 font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2913">
                Scopri Greenback
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-10" data-id="element-2914">
        <div className="container mx-auto px-4 text-center" data-id="element-2915">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className="h-10 w-auto object-contain mx-auto mb-4" data-id="element-2916" />
          <p className="text-gray-600 text-sm font-normal" data-id="element-2917">
            © 2024 Greenback - Piattaforma per la Sostenibilità
          </p>
          <p className="text-gray-500 text-xs mt-2 font-normal" data-id="element-2918">
            Tutti i diritti riservati
          </p>
        </div>
      </footer>
    </div>;
};
interface KpiCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  trend: string;
  trendUp?: boolean;
}
const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  value,
  label,
  trend,
  trendUp
}) => {
  return <div className="px-4 py-5 flex flex-col items-center text-center" data-id="element-2919">
      <div className="p-2 bg-green-50 rounded-full text-green-600 mb-2" data-id="element-2920">
        {icon}
      </div>
      <div className="text-2xl font-semibold text-gray-800 mb-1" data-id="element-2921">{value}</div>
      <div className="text-sm text-gray-600 font-normal" data-id="element-2922">{label}</div>
      <div className={`text-xs ${trendUp ? 'text-green-600' : 'text-gray-500'} mt-1 font-normal`} data-id="element-2923">
        {trend}
      </div>
    </div>;
};
interface GroupItemProps {
  name: string;
  members: number;
}
const GroupItem: React.FC<GroupItemProps> = ({
  name,
  members
}) => {
  return <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-2924">
      <div className="flex items-center" data-id="element-2925">
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 mr-3" data-id="element-2926">
          <UsersIcon size={16} strokeWidth={1.5} data-id="element-2927" />
        </div>
        <span className="font-medium text-gray-700 text-sm" data-id="element-2928">{name}</span>
      </div>
      <span className="text-sm text-gray-500 font-normal" data-id="element-2929">
        {members} membri
      </span>
    </div>;
};
export default PublicProfile;